import { Form, formService } from './formService';
import { Appointment } from './appointmentService';
import { documentService } from './documentService';
import { PDFService } from './PDFService';
import { Job } from './jobService';
import { Customer } from './customerService';
import { convertBytesToBase64, generatePdfFilename } from '../utils/pdfHelpers';
import { getFirstAppointmentDates } from '../utils/appointmentHelpers';

/**
 * Parses PDF configuration from form's pdfConfigurationJson
 */
const parsePdfConfig = (form: Form): any => {
  if (!form.pdfConfigurationJson) return {};

  try {
    return JSON.parse(form.pdfConfigurationJson);
  } catch (error) {
    console.warn('Failed to parse PDF configuration JSON:', error);
    return {};
  }
};

/**
 * Generates a PDF with the given form data and options
 */
const generateFormPdf = async (
  form: Form,
  job: Job,
  customer: Customer | null,
  appointments: Appointment[],
  formData: { [key: string]: any },
  formParameterValues: { [key: string]: any },
  userDataFields?: any[]
): Promise<Uint8Array> => {
  const pdfConfig = parsePdfConfig(form);
  const filename = generatePdfFilename(job, form);

  const headerData: { [key: string]: string | number | boolean | null | undefined } = {
    DisplayName: job.assignedSalesPersonName || ''
  };

  const firstAppointment = getFirstAppointmentDates(appointments);

  return PDFService.generateEditablePDF(
    formData,
    form.description,
    form.formSections || [],
    {
      filename,
      headerConfigurationJson: form.headerConfigurationJson,
      headerHeight: pdfConfig.headerHeight ?? 100,
      pageMargin: pdfConfig.pageMargin ?? 15,
      headerData,
      defaultFontSize: pdfConfig.defaultFontSize,
      labelFontSize: pdfConfig.labelFontSize,
      multilineFontSize: pdfConfig.multilineFontSize,
      sectionTitleFontSize: pdfConfig.sectionTitleFontSize,
      fieldSpacing: pdfConfig.fieldSpacing,
      sectionSpacing: pdfConfig.sectionSpacing,
      checkboxListSpacing: pdfConfig.checkboxListSpacing,
      sectionPadding: pdfConfig.sectionPadding,
      fieldHeight: pdfConfig.fieldHeight,
      showSectionBorders: pdfConfig.showSectionBorders,
      checkboxListColumns: pdfConfig.checkboxListColumns,
      pixelVersion: true,
      job,
      customer,
      firstAppointment,
      formParameterValues,
      userDataFields
    }
  );
};

/**
 * Saves form data ONLY (no PDF generation).
 * Used for auto-save functionality where PDF is generated lazily.
 */
export const handleSaveFormDataOnly = async (
  form: Form,
  jobId: string,
  formData: { [key: string]: any },
  loadFormResponses: (formId: number, updateState?: boolean, forceRefresh?: boolean) => Promise<any>
): Promise<void> => {
  if (!jobId) {
    return;
  }

  try {
    // Merge with existing data to prevent wiping out fields not in the current PDF view
    // Don't update state here to avoid UI flickering during merge
    const existingFormData = await loadFormResponses(form.id, false) || {};
    const mergedFormData = { ...existingFormData, ...(formData || {}) };

    // If we have data, save it to the API
    if (Object.keys(mergedFormData).length > 0) {
      await formService.submitFormResponse(form, jobId, mergedFormData);
      // Refresh the cache but DON'T update local state (updateState: false)
      // This prevents the field value "flash" bug where the old value briefly appears
      // because the server response momentarily overwrites the local state
      await loadFormResponses(form.id, false, true);
    } else {
      return;
    }

    // NOTE: NO PDF generation here - that happens lazily when Documents tab is opened
  } catch (error) {
    console.error('Error saving form data:', error);
    throw error;
  }
};

/**
 * Saves form data and uploads PDF to Azure blob storage (save only, no download)
 */
export const handleSavePixelVersion = async (
  form: Form,
  job: Job | null,
  customer: Customer | null,
  appointments: Appointment[],
  loadFormResponses: (formId: number, updateState?: boolean, forceRefresh?: boolean) => Promise<any>,
  capturedFormData?: { [key: string]: any },
  userDataFields?: any[],
  onDocumentSaved?: () => void
): Promise<void> => {
  if (!job) {
    return;
  }

  try {
    // Merge with existing data to prevent wiping out fields not in the current PDF view
    const existingFormData = await loadFormResponses(form.id) || {};
    const formData = { ...existingFormData, ...(capturedFormData || {}) };

    // If we have data, save it to the API
    if (Object.keys(formData).length > 0) {
      await formService.submitFormResponse(form, job.id, formData);
      // Force refresh form responses to update the cache for all forms
      // This ensures other forms that depend on this form's data are updated
      await loadFormResponses(form.id, true, true);
    } else {
      return;
    }

    // Generate and upload PDF to Azure blob storage
    try {
      const formParameterValues = await formService.getFormFieldParameterValues(job.id, form.id);

      const pdfBytes = await generateFormPdf(
        form,
        job,
        customer,
        appointments,
        formData,
        formParameterValues,
        userDataFields
      );

      // Convert PDF bytes to base64 for upload
      const base64Pdf = await convertBytesToBase64(pdfBytes);

      // Upload PDF to Azure blob storage
      await documentService.storeFormPdf({
        jobId: job.id,
        formId: form.id,
        formName: form.description,
        jobNumber: job.jobNumber || '',
        pdfContent: base64Pdf
      });

      // Notify caller that document was saved (so they can refresh the documents list)
      onDocumentSaved?.();
    } catch (pdfError) {
      console.error('Error generating/uploading PDF:', pdfError);
      // Don't throw - we still want the form data to be saved even if PDF upload fails
    }
  } catch (error) {
    console.error('Error saving form data:', error);
    throw error;
  }
};

/**
 * Downloads/prints PDF and saves form data
 */
export const handleDownloadAndSavePixelVersion = async (
  form: Form,
  job: Job | null,
  customer: Customer | null,
  appointments: Appointment[],
  loadFormResponses: (formId: number, updateState?: boolean, forceRefresh?: boolean) => Promise<any>,
  capturedFormData?: { [key: string]: any },
  shouldPrint: boolean = false,
  userDataFields?: any[]
): Promise<string> => {
  if (!job) {
    throw new Error('No job provided');
  }

  try {
    // Fetch form parameter values for preloading
    const formParameterValues = await formService.getFormFieldParameterValues(job.id, form.id);

    // Get current form responses and merge with captured data
    const existingFormData = await loadFormResponses(form.id) || {};
    const formData = { ...existingFormData, ...(capturedFormData || {}) };

    // If we have data, save it to the API first
    if (Object.keys(formData).length > 0) {
      try {
        await formService.submitFormResponse(form, job.id, formData);
        // Force refresh form responses to update the cache for all forms
        await loadFormResponses(form.id, true, true);
      } catch (saveError) {
        console.error('Error saving form data:', saveError);
        // Continue with PDF generation even if save fails
      }
    }

    const filename = generatePdfFilename(job, form);

    // Generate PDF with merged data
    const pdfBytes = await generateFormPdf(
      form,
      job,
      customer,
      appointments,
      formData,
      formParameterValues,
      userDataFields
    );

    // Create blob
    const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    if (shouldPrint) {
      // Create a hidden iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        // Give it some time to open the print dialog before cleaning up
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(url);
        }, 60000);
      };

      return 'Print dialog opened successfully with form data saved';
    } else {
      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up URL after download
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10000);

      return 'PDF downloaded successfully with form data saved';
    }
  } catch (err: any) {
    console.error('Error downloading PDF:', err);
    throw new Error(err.message || 'Failed to download PDF');
  }
};

/**
 * Checks if a form has PixelVersion enabled
 */
export const isPixelVersionForm = (form: Form): boolean => {
  if (!form.pdfConfigurationJson) return false;

  try {
    const pdfConfig = JSON.parse(form.pdfConfigurationJson);
    return pdfConfig.pixelVersion === true;
  } catch (error) {
    return false;
  }
};

/**
 * Generates and stores a form PDF to Azure blob storage.
 * Used for lazy PDF generation when Documents tab is opened.
 */
export const generateAndStoreFormPdf = async (
  form: Form,
  job: Job,
  customer: Customer | null,
  appointments: Appointment[],
  formData: { [key: string]: any },
  formParameterValues: { [key: string]: any },
  userDataFields?: any[]
): Promise<void> => {
  try {
    const pdfBytes = await generateFormPdf(
      form,
      job,
      customer,
      appointments,
      formData,
      formParameterValues,
      userDataFields
    );

    // Convert PDF bytes to base64 for upload
    const base64Pdf = await convertBytesToBase64(pdfBytes);

    // Upload PDF to Azure blob storage
    await documentService.storeFormPdf({
      jobId: job.id,
      formId: form.id,
      formName: form.description,
      jobNumber: job.jobNumber || '',
      pdfContent: base64Pdf
    });
  } catch (error) {
    console.error('Error generating/storing PDF:', error);
    throw error;
  }
};
